import React from "react";
import aiImage from "../../../assets/Digital-Transformation.jpg.webp";
import strategyImage1 from "../../../assets/Digital-Transformation3.webp";
import strategyImage from "../../../assets/Digital-Transformation1.webp";

export default function DigitalTransformation() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] sm:h-[70vh] md:h-[800px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={aiImage}
            alt="Strategy & Digital Transformation"
            className="w-full h-full object-cover sm:object-contain md:object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
            Strategy & Digital Transformation
          </h1>
          <p className="text-sm sm:text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed">
            Shape business models and redefine service delivery using digital tools
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
            {/* Image - Left Side */}
            <div className="flex-1 w-full">
              <img
                src={strategyImage}
                alt="Strategy & Digital Transformation"
                className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-lg shadow-lg"
              />
            </div>

            {/* Content - Right Side */}
            <div className="flex-1 w-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 text-[#482A7A] text-center lg:text-left">
                Strategy & Digital Transformation
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6 text-center lg:text-left">
                Shape business models, redefine service delivery using digital tools, and guide leadership through change journeys.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed text-center lg:text-left">
                We help organizations navigate digital transformation by developing strategic roadmaps that align technology initiatives with business objectives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Transformation Strategy Services Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
            {/* Content - Left Side */}
            <div className="flex-1 w-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-[#482A7A] text-center lg:text-left">
                Digital Transformation Strategy Services
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 text-center lg:text-left mb-6">
                In today's rapidly evolving digital economy, businesses must embrace digital transformation to remain competitive, enhance customer experiences, and drive operational efficiency. A well-defined Digital Transformation Strategy ensures that organizations leverage cutting-edge technologies to innovate, streamline processes, and achieve long-term growth.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 text-center lg:text-left">
                ALIF, a leading digital transformation consultancy, specializes in helping businesses navigate their digital journey with tailored strategies, seamless technology integration, and measurable results. This explores the importance of digital transformation, key components of a successful strategy, and how ALIF empowers businesses to thrive in the digital age.
              </p>
            </div>

            {/* Image - Right Side */}
            <div className="flex-1 w-full">
              <img
                src={strategyImage1}
                alt="Digital Transformation Strategy"
                className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Importance of Digital Transformation Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-[#482A7A] text-center">
            The Importance of Digital Transformation
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-12 text-center max-w-4xl mx-auto">
            Digital transformation is no longer optional—it is a necessity for businesses looking to stay relevant in an increasingly digital-first world. Organizations must position themselves as leaders in digital innovation, driving the adoption of advanced technologies across industries.
          </p>
          
          <div className="flex flex-wrap justify-center gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-blue-600 w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-lg font-bold text-blue-600 mb-4">1. Enhancing Customer Experience</h3>
              <p className="text-sm text-gray-600">
                Today's consumers demand seamless, personalized, and instant interactions. Digital transformation enables businesses to leverage AI-driven chatbots, omnichannel engagement, and data analytics to deliver superior customer experiences.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-green-600 w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-lg font-bold text-green-600 mb-4">2. Improving Operational Efficiency</h3>
              <p className="text-sm text-gray-600">
                Automation, cloud computing, and IoT-driven solutions streamline workflows, reduce manual errors, and optimize resource allocation, leading to significant cost savings and productivity gains.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-purple-600 w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-lg font-bold text-purple-600 mb-4">3. Enabling Data-Driven Decision Making</h3>
              <p className="text-sm text-gray-600">
                Advanced analytics and business intelligence tools provide actionable insights, helping organizations make informed decisions, predict market trends, and improve strategic planning.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-orange-600 w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-lg font-bold text-orange-600 mb-4">4. Strengthening Cybersecurity and Compliance</h3>
              <p className="text-sm text-gray-600">
                With increasing cyber threats and strict regulatory requirements, digital transformation must include robust security measures to protect sensitive data and ensure compliance.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-red-600 w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-lg font-bold text-red-600 mb-4">5. Fostering Innovation and Agility</h3>
              <p className="text-sm text-gray-600">
                Businesses that embrace digital transformation can quickly adapt to market changes, launch new products faster, and stay ahead of competitors through continuous innovation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Components Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-white">
              Key Components of a Successful Digital Transformation Strategy
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-200 max-w-4xl mx-auto">
              A well-structured digital transformation strategy goes beyond technology adoption—it requires a holistic approach that aligns people, processes, and tools with business objectives. Below are the essential components:
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Row 1 */}
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">Vision and Leadership Alignment</h3>
              <p className="text-gray-200 text-sm text-center">
                Successful digital transformation starts with a clear vision from leadership. ALIF works closely with C-suite executives to define business goals and ensure technology initiatives support long-term growth.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">Customer-Centric Approach</h3>
              <p className="text-gray-200 text-sm text-center">
                Understanding customer needs is critical. ALIF helps businesses implement customer journey mapping, AI-driven personalization, and seamless digital touchpoints to enhance engagement.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">Technology Modernization</h3>
              <p className="text-gray-200 text-sm text-center">
                Legacy systems often hinder digital progress. ALIF assists in modernizing IT infrastructure with cloud migration, AI, IoT, and blockchain to improve scalability and performance.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">Process Automation and Optimization</h3>
              <p className="text-gray-200 text-sm text-center">
                Repetitive tasks slow down operations. ALIF integrates Robotic Process Automation (RPA) and workflow automation to reduce manual efforts and enhance efficiency.
              </p>
            </div>
            
            {/* Row 2 */}
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">5</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">Data and Analytics Integration</h3>
              <p className="text-gray-200 text-sm text-center">
                Data is the backbone of digital transformation. ALIF deploys advanced analytics, predictive modeling, and real-time reporting to turn data into actionable business insights.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">6</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">Cybersecurity and Risk Management</h3>
              <p className="text-gray-200 text-sm text-center">
                As digital adoption grows, so do cyber threats. ALIF implements multi-layered security frameworks, including zero-trust architecture, encryption, and compliance audits, to safeguard business assets.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">7</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">Change Management and Workforce Upskilling</h3>
              <p className="text-gray-200 text-sm text-center">
                Employees must adapt to new technologies. ALIF provides training programs, change management strategies, and digital literacy initiatives to ensure smooth adoption.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">8</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">Continuous Improvement and Scalability</h3>
              <p className="text-gray-200 text-sm text-center">
                Digital transformation is an ongoing journey. ALIF establishes performance metrics, KPIs, and iterative improvement processes to ensure long-term success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How ALIF Supports Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-[#482A7A]">
              How ALIF Supports Digital Transformation
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 max-w-4xl mx-auto">
              ALIF is a trusted partner for businesses seeking to accelerate their digital transformation journey. With deep industry expertise and a proven methodology, ALIF delivers end-to-end digital strategy services tailored to each client's unique needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-blue-700 mb-3">Digital Readiness Assessment</h3>
              <p className="text-sm text-gray-600">
                Before embarking on transformation, businesses must evaluate their current digital maturity. ALIF conducts comprehensive audits to identify gaps, opportunities, and the best path forward.
              </p>
            </div>
            
            <div className="relative bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-green-700 mb-3">Customized Digital Roadmap Development</h3>
              <p className="text-sm text-gray-600">
                Every business has different goals. ALIF designs tailored digital transformation roadmaps with clear milestones, budgets, and timelines to ensure alignment with business objectives.
              </p>
            </div>
            
            <div className="relative bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-purple-700 mb-3">Cloud and AI-Driven Solutions</h3>
              <p className="text-sm text-gray-600">
                Cloud computing and AI are at the heart of digital transformation. ALIF helps businesses migrate to AWS, Azure, or hybrid cloud environments while integrating AI-powered automation, chatbots, and predictive analytics.
              </p>
            </div>
            
            <div className="relative bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-orange-700 mb-3">Intelligent Process Automation</h3>
              <p className="text-sm text-gray-600">
                Manual processes slow down growth. ALIF implements RPA, workflow automation, and smart ERP solutions to enhance efficiency and reduce operational costs.
              </p>
            </div>
            
            <div className="relative bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-sm">5</div>
              <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-red-700 mb-3">Enhanced Data and Business Intelligence</h3>
              <p className="text-sm text-gray-600">
                Unlocking data potential is key. ALIF deploys big data analytics, AI-driven insights, and dashboard reporting to empower decision-makers with real-time intelligence.
              </p>
            </div>
            
            <div className="relative bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">6</div>
              <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-indigo-700 mb-3">Cybersecurity and Compliance Assurance</h3>
              <p className="text-sm text-gray-600">
                Protecting digital assets is non-negotiable. ALIF builds cyber-resilient frameworks, conducts vulnerability assessments, and ensures compliance with data protection laws.
              </p>
            </div>
            
            <div className="relative bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm">7</div>
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-teal-700 mb-3">Digital Customer Experience Optimization</h3>
              <p className="text-sm text-gray-600">
                Customer expectations are evolving. ALIF enhances e-commerce platforms, mobile apps, and omnichannel engagement to deliver seamless, personalized experiences.
              </p>
            </div>
            
            <div className="relative bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group">
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center font-bold text-sm">8</div>
              <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-pink-700 mb-3">Change Management and Employee Training</h3>
              <p className="text-sm text-gray-600">
                Technology adoption requires cultural change. ALIF provides workshops, upskilling programs, and leadership coaching to drive organizational buy-in and digital fluency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose ALIF Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 bg-gradient-to-br from-slate-900 via-gray-900 to-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-white">
              Why Choose ALIF for Digital Transformation?
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mb-8"></div>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8">
            {/* Hexagonal Badge 1 */}
            <div className="relative group">
              <div className="w-48 h-48 bg-gradient-to-br from-blue-500 to-blue-700 transform rotate-45 rounded-3xl flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300">
                <div className="transform -rotate-45 text-center">
                  <svg className="w-12 h-12 text-white mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <h4 className="text-white font-bold text-sm">Industry Expertise</h4>
                </div>
              </div>
              <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 w-64 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-gray-300 text-xs bg-black/50 p-3 rounded-lg backdrop-blur-sm">
                  Tailored solutions for finance, healthcare, retail, logistics, and government sectors.
                </p>
              </div>
            </div>
            
            {/* Hexagonal Badge 2 */}
            <div className="relative group">
              <div className="w-48 h-48 bg-gradient-to-br from-green-500 to-green-700 transform rotate-45 rounded-3xl flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300">
                <div className="transform -rotate-45 text-center">
                  <svg className="w-12 h-12 text-white mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="text-white font-bold text-sm">End-to-End Support</h4>
                </div>
              </div>
              <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 w-64 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-gray-300 text-xs bg-black/50 p-3 rounded-lg backdrop-blur-sm">
                  Full lifecycle support from strategy to execution with minimal disruption.
                </p>
              </div>
            </div>
            
            {/* Hexagonal Badge 3 */}
            <div className="relative group">
              <div className="w-48 h-48 bg-gradient-to-br from-purple-500 to-purple-700 transform rotate-45 rounded-3xl flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300">
                <div className="transform -rotate-45 text-center">
                  <svg className="w-12 h-12 text-white mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <h4 className="text-white font-bold text-sm">Proven Success</h4>
                </div>
              </div>
              <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 w-64 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-gray-300 text-xs bg-black/50 p-3 rounded-lg backdrop-blur-sm">
                  Strong track record combining local knowledge with global best practices.
                </p>
              </div>
            </div>
            
            {/* Hexagonal Badge 4 */}
            <div className="relative group">
              <div className="w-48 h-48 bg-gradient-to-br from-orange-500 to-orange-700 transform rotate-45 rounded-3xl flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300">
                <div className="transform -rotate-45 text-center">
                  <svg className="w-12 h-12 text-white mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h4 className="text-white font-bold text-sm">Tech Partnerships</h4>
                </div>
              </div>
              <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 w-64 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-gray-300 text-xs bg-black/50 p-3 rounded-lg backdrop-blur-sm">
                  Partnerships with Microsoft, SAP, Oracle, AWS for cutting-edge solutions.
                </p>
              </div>
            </div>
            
            {/* Hexagonal Badge 5 */}
            <div className="relative group">
              <div className="w-48 h-48 bg-gradient-to-br from-red-500 to-red-700 transform rotate-45 rounded-3xl flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300">
                <div className="transform -rotate-45 text-center">
                  <svg className="w-12 h-12 text-white mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <h4 className="text-white font-bold text-sm">Measurable ROI</h4>
                </div>
              </div>
              <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 w-64 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-gray-300 text-xs bg-black/50 p-3 rounded-lg backdrop-blur-sm">
                  Data-driven approach ensuring tangible ROI through cost savings and growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}